import { lazy, Suspense } from 'react';
import { RouteObject } from 'react-router';
import AuthGuard from 'components/AuthGuard';
import DashboardLayout from 'components/dashboard/DashboardLayout';
import GuestGuard from 'components/GuestGuard';
import LoadingScreen from 'components/LoadingScreen';
import MainLayout from 'components/MainLayout';
import CallBack from 'pages/authentication/login/Callback';
import PeopleTemplateForm from './pages/products/onboarding/templates/create/PeopleTemplateForm';

const Loadable = (Component: any) => (props: any) => (
    <Suspense fallback={<LoadingScreen/>}>
        <Component {...props} />
    </Suspense>
);

// Authentication pages
const Login = Loadable(lazy(() => import('pages/authentication/Login')));

// General pages
const ConsentRequests = Loadable(lazy(() => import('pages/products/consent/ConsentRequests')));
const ConsentDetailsPage = Loadable(lazy(() => import('pages/products/consent/ConsentDetailsPage')));
const BulkConsentRequests = Loadable(lazy(() => import('pages/products/consent/bulk/BulkConsentRequests')));
const BulkConsentCreator = Loadable(lazy(() => import('pages/products/consent/bulk/BulkConsentCreator')));
const BulkConsentDetails = Loadable(lazy(() => import('pages/products/consent/bulk/BulkConsentDetails')));
const ConsentTemplateList = Loadable(lazy(() => import('pages/products/consent/templates/ConsentTemplates')));
const ConsentTemplateCreator = Loadable(lazy(() => import('pages/products/consent/templates/create/TemplateCreator')));

const DataCustodianList = Loadable(lazy(() => import('pages/products/consent/data-custodians/DataCustodians')));

const IdVerificationRequestList = Loadable(lazy(() => import('pages/products/id-verification/IdVerificationRequests')));
const BulkIdVerifications = Loadable(lazy(() => import('pages/products/id-verification/bulk/BulkIdVerifications')));
const BulkIdVerificationCreator = Loadable(lazy(() => import('pages/products/id-verification/bulk/BulkIdVerificationCreator')));
const BulkIdVerificationDetails = Loadable(lazy(() => import('pages/products/id-verification/bulk/BulkIdVerificationDetails')));

const PhoneNumberVerificationRequests = Loadable(lazy(() => import('pages/products/phone-number-verification/PhoneNumberVerificationRequests')));
const BulkPhoneNumberVerifications = Loadable(lazy(() => import('pages/products/phone-number-verification/bulk/BulkPhoneNumberVerifications')));
const BulkPhoneNumberVerificationCreator = Loadable(lazy(() => import('pages/products/phone-number-verification/bulk/BulkPhoneNumberVerificationCreator')));
const BulkPhoneNumberVerificationDetails = Loadable(lazy(() => import('pages/products/phone-number-verification/bulk/BulkPhoneNumberVerificationDetails')));

const FaceVerificationRequests = Loadable(lazy(() => import('pages/products/liveness-detection/LivenessDetectionRequests')));
const BulkFaceVerifications = Loadable(lazy(() => import('pages/products/liveness-detection/bulk/BulkLivenessDetection')));
const BulkFaceVerificationCreator = Loadable(lazy(() => import('pages/products/liveness-detection/bulk/BulkLivenessDetectionCreator')));
const BulkFaceVerificationDetails = Loadable(lazy(() => import('pages/products/liveness-detection/bulk/BulkLivenessDetectionDetails')));

// faceMatch
const FaceMatchRequests = Loadable(lazy(() => import('pages/products/face-match/FaceMatchRequests')));

const TinVerificationRequests = Loadable(lazy(() => import('pages/products/tin-verification/TinVerificationRequests')));

const AmlScreeningRequests = Loadable(lazy(() => import('pages/products/aml-screening/AmlScreenings')));
const BulkAmlScreeningRequests = Loadable(lazy(() => import('pages/products/aml-screening/bulk/BulkAmlScreenings')));
const BulkAmlScreeningDetails = Loadable(lazy(() => import('pages/products/aml-screening/bulk/BulkAmlScreeningDetails')));
const BulkAmlScreeningCreator = Loadable(lazy(() => import('pages/products/aml-screening/bulk/BulkAmlScreeningCreator')));

const BusinessSearchRequests = Loadable(lazy(() => import('pages/products/company-search/CompanySearchRequests')));
const BulkBusinessSearchRequests = Loadable(lazy(() => import('pages/products/company-search/bulk/BulkCompanySearchRequests')));
const BulkBusinessSearchDetails = Loadable(lazy(() => import('pages/products/company-search/bulk/BulkCompanySearchDetails')));
const BulkBusinessSearchCreator = Loadable(lazy(() => import('pages/products/company-search/bulk/BulkCompanySearchCreator')));

// Customer pages
const PeopleList = Loadable(lazy(() => import('pages/customers/people/People')));
const PeopleUpload = Loadable(lazy(() => import('pages/customers/people/UploadPeople')));
const PeopleBulkImports = Loadable(lazy(() => import('pages/customers/people/bulk-imports/PeopleBulkImports')));
const PeopleBulkImportDetails = Loadable(lazy(() => import('pages/customers/people/bulk-imports/PeopleBulkImportDetails')));

// onboarding templates
const PeopleTemplateLayout = Loadable(lazy(() => import('pages/products/onboarding/templates/create/PeopleTemplateForm')));
const PeopleTemplateList = Loadable(lazy(() => import('pages/products/onboarding/templates/PeopleTemplates')));

// onboarding cases
const OnboardingCases = Loadable(lazy(() => import('pages/products/onboarding/cases/CaseRequests')));
const BulkOnboardingRequests = Loadable(lazy(() => import('pages/products/onboarding/bulk/BulkOnboardingRequests')));
const BulkOnboardingCreator = Loadable(lazy(() => import('pages/products/onboarding/bulk/BulkOnboardingCreator')));
const BulkOnboardingRequestDetails = Loadable(lazy(() => import('pages/products/onboarding/bulk/BulkOnboardingDetails')));

const CompaniesList = Loadable(lazy(() => import('pages/customers/companies/Companies')));
const CompaniesUpload = Loadable(lazy(() => import('pages/customers/companies/UploadCompanies')));
const CustomerDetails = Loadable(lazy(() => import('pages/customers/common-components/customer-profile/CustomerDetails')));
const CompaniesBulkImports = Loadable(lazy(() => import('pages/customers/companies/bulk-imports/CompaniesBulkImports')));
const CompaniesBulkImportDetails = Loadable(lazy(() => import('pages/customers/companies/bulk-imports/CompaniesBulkImportDetails')));

// Admin pages
const PartnersList = Loadable(lazy(() => import('pages/management/partners/Partners')));
const StaffList = Loadable(lazy(() => import('pages/management/staff/Staff')));
const StaffDetails = Loadable(lazy(() => import('pages/management/staff/details/StaffDetails')));
const Settings = Loadable(lazy(() => import('pages/management/settings/Settings')));
const Invoices = Loadable(lazy(() => import('pages/management/invoices/Invoices')));
const PriceBandsList = Loadable(lazy(() => import('pages/management/price-bands/PriceBands')));

// Error pages
const AuthorizationRequired = Loadable(lazy(() => import('pages/AuthorizationRequired')));
const NotFound = Loadable(lazy(() => import('pages/NotFound')));
const ServerError = Loadable(lazy(() => import('pages/ServerError')));

// Profile
const Profile = Loadable(lazy(() => import('pages/account/Profile')));

const routes: RouteObject[] = [
  {
    path: 'authentication',
    children: [
      {
        path: 'login',
        element: (
                    <GuestGuard>
                        <Login/>
                    </GuestGuard>
        )
      },
      {
        path: 'login-unguarded',
        element: <Login/>
      },
      {
        path: 'login-callback',
        element: <CallBack/>
      }
    ]
  },
  {
    path: '*',
    element: (
            <AuthGuard>
                <DashboardLayout/>
            </AuthGuard>
    ),
    children: [
      {
        path: '',
        element: <PeopleList/>
      },
      {
        path: 'profile',
        element: <Profile/>
      },
      {
        path: 'people/profiles',
        children: [
          {
            path: '',
            element: <PeopleList/>
          },
          {
            path: ':customerId',
            element: <CustomerDetails/>
          },
          {
            path: 'upload',
            element: <PeopleUpload/>
          }
        ]
      },
      {
        path: 'people/bulk-imports',
        children: [
          {
            path: '',
            element: <PeopleBulkImports/>
          },
          {
            path: ':bulkId',
            element: <PeopleBulkImportDetails/>
          }
        ]
      },
      {
        path: 'companies/profiles',
        children: [
          {
            path: '',
            element: <CompaniesList/>
          },
          {
            path: ':customerId',
            element: <CustomerDetails/>
          },
          {
            path: 'upload',
            element: <CompaniesUpload/>
          }
        ]
      },
      {
        path: 'companies/bulk-imports',
        children: [
          {
            path: '',
            element: <CompaniesBulkImports/>
          },
          {
            path: ':bulkId',
            element: <CompaniesBulkImportDetails/>
          }
        ]
      },
      {
        path: 'consent/requests',
        children: [
          {
            path: '',
            element: <ConsentRequests/>
          },
          {
            path: ':consentId',
            element: <ConsentDetailsPage/>
          },
        ]
      },
      {
        path: 'consent/bulk-requests',
        children: [
          {
            path: '',
            element: <BulkConsentRequests/>
          },
          {
            path: 'create',
            element: <BulkConsentCreator/>
          },
          {
            path: ':bulkId',
            element: <BulkConsentDetails/>
          },
        ]
      },
      {
        path: 'onboarding',
        children: [
          {
            path: '',
            element: <OnboardingCases/>
          },
          {
            path: 'cases',
            element: <OnboardingCases/>
          },
          {
            path: 'bulk-requests',
            children: [
              {
                path: '',
                element: <BulkOnboardingRequests/>
              },
              {
                path: ':bulkId',
                element: <BulkOnboardingRequestDetails/>
              }
            ]
          },
          {
            path: 'bulk-create',
            element: <BulkOnboardingCreator/>
          }
        ]
      },
      {
        path: 'onboarding/templates',
        children: [
          {
            path: '',
            element: <PeopleTemplateList/>
          },
          {
            path: 'create',
            element: <PeopleTemplateLayout/>
          },
          {
            path: ':templateId',
            element: <PeopleTemplateForm/>
          },
        ]
      },
      {
        path: 'consent/templates',
        children: [
          {
            path: '',
            element: <ConsentTemplateList/>
          },
          {
            path: 'create',
            element: <ConsentTemplateCreator/>
          },
          {
            path: ':templateId',
            element: <ConsentTemplateCreator/>
          },
          {
            path: 'data-custodians',
            element: <DataCustodianList/>
          },
        ]
      },
      {
        path: 'consent/data-custodians',
        children: [
          {
            path: '',
            element: <DataCustodianList/>
          },
        ]
      },
      {
        path: 'national-id-verification/requests',
        children: [
          {
            path: '',
            element: <IdVerificationRequestList/>
          },
        ]
      },
      {
        path: 'national-id-verification/bulk-requests',
        children: [
          {
            path: '',
            element: <BulkIdVerifications/>
          },
          {
            path: 'create',
            element: <BulkIdVerificationCreator/>
          },
          {
            path: ':bulkId',
            element: <BulkIdVerificationDetails/>
          }
        ]
      },

      {
        path: 'phone-number-verification/requests',
        children: [
          {
            path: '',
            element: <PhoneNumberVerificationRequests/>
          }
        ]
      },
      {
        path: 'phone-number-verification/bulk-requests',
        children: [
          {
            path: '',
            element: <BulkPhoneNumberVerifications/>
          },
          {
            path: 'create',
            element: <BulkPhoneNumberVerificationCreator/>
          },
          {
            path: ':bulkId',
            element: <BulkPhoneNumberVerificationDetails/>
          }
        ]
      },

      {
        path: 'face-verification/requests',
        children: [
          {
            path: '',
            element: <FaceVerificationRequests/>
          }
        ]
      },
      {
        path: 'face-verification/bulk-requests',
        children: [
          {
            path: '',
            element: <BulkFaceVerifications/>
          },
          {
            path: 'create',
            element: <BulkFaceVerificationCreator/>
          },
          {
            path: ':bulkId',
            element: <BulkFaceVerificationDetails/>
          }
        ]
      },
      {
        path: 'face-match/requests',
        children: [
          {
            path: '',
            element: <FaceMatchRequests/>
          }
        ]
      },
      {
        path: 'tin-verification',
        children: [
          {
            path: '',
            element: <TinVerificationRequests/>
          }
        ]
      },
      {
        path: 'aml-screening/requests',
        children: [
          {
            path: '',
            element: <AmlScreeningRequests/>
          },
        ]
      },
      {
        path: 'aml-screening/bulk-requests',
        children: [
          {
            path: '',
            element: <BulkAmlScreeningRequests/>
          },
          {
            path: 'create',
            element: <BulkAmlScreeningCreator/>
          },
          {
            path: ':bulkId',
            element: <BulkAmlScreeningDetails/>
          }
        ]
      },
      {
        path: 'business-search/requests',
        children: [
          {
            path: '',
            element: <BusinessSearchRequests/>
          }
        ]
      },
      {
        path: 'business-search/bulk-requests',
        children: [
          {
            path: '',
            element: <BulkBusinessSearchRequests/>
          },
          {
            path: 'create',
            element: <BulkBusinessSearchCreator/>
          },
          {
            path: ':bulkId',
            element: <BulkBusinessSearchDetails/>
          }
        ]
      },
      {
        path: 'staff',
        children: [
          {
            path: '',
            element: <StaffList/>
          },
          {
            path: ':staffId',
            element: <StaffDetails/>
          }
        ]
      },
      {
        path: 'partners',
        children: [
          {
            path: '',
            element: <PartnersList/>
          },
          {
            path: ':partnerId',
            element: <Settings/>
          }
        ]
      },
      {
        path: 'settings',
        children: [
          {
            path: '',
            element: <Settings/>
          },
          {
            path: ':partnerId',
            element: <Settings/>
          }
        ]
      },
      {
        path: 'invoices',
        element: <Invoices/>
      },
      {
        path: 'price-bands',
        element: <PriceBandsList/>
      },
    ]
  },
  {
    path: 'error',
    element: <MainLayout/>,
    children: [
      {
        path: '401',
        element: <AuthorizationRequired/>
      },
      {
        path: '404',
        element: <NotFound/>
      },
      {
        path: '500',
        element: <ServerError/>
      },
      {
        path: '*',
        element: <NotFound/>
      }
    ]
  }
];

export default routes;
